import type { ResolutionTargetLiteral } from "./resolveWithValue.helpers";

import { copiesLeftDisagreeing, type CopyKey, type OutcomeRow } from "./resolveWithValue.outcome";
import { describeWasteLines } from "./vendorLineAddition";

const DISAGREEING_NAMES: Record<CopyKey, string> = {
  vendor: "vendor data",
  eManifest: "e-Manifest",
  document: "the manifest document",
};

function writtenPrefix(applyTo: readonly ResolutionTargetLiteral[]): string | null {
  const vendor = applyTo.includes("VENDOR_DATA");
  const document = applyTo.includes("DOCUMENT");
  if (vendor && document) return "Vendor and manifest document updated.";
  if (vendor) return "Vendor updated.";
  if (document) return "Manifest document updated.";
  return null;
}

/** Names the copies that keep a row in Pending Review; the prediction only supplies the names. */
function stayOpenSentence(
  row: OutcomeRow,
  answerValue: string | null,
  applyTo: readonly ResolutionTargetLiteral[]
): string {
  const names = copiesLeftDisagreeing(row, answerValue, applyTo).map(
    (copy) => DISAGREEING_NAMES[copy]
  );
  if (names.length === 0) return "Row stays Pending Review.";
  const verb = names.length === 1 ? "disagrees" : "disagree";
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names[0];
  return `Row stays Pending Review because ${list} still ${verb}.`;
}

/**
 * The toast for a single-row value save. `savedStatus` is the status the
 * server returned for the row, so the toast states what happened rather than
 * a client-side guess.
 */
export function singleRowSaveToast(
  row: OutcomeRow,
  answerValue: string | null,
  applyTo: readonly ResolutionTargetLiteral[],
  savedStatus: string | null | undefined
): string {
  let sentence = "";
  if (savedStatus === "RESOLVED") sentence = "Row is Resolved.";
  else if (savedStatus === "PENDING") {
    sentence = "Row is Fix pending until e-Manifest is corrected in RCRAInfo.";
  } else if (savedStatus === "UNRESOLVED") sentence = stayOpenSentence(row, answerValue, applyTo);
  return [writtenPrefix(applyTo) ?? "Saved.", sentence].filter(Boolean).join(" ");
}

/** The toast after adding the e-Manifest line to vendor data, e.g. "Added line 3 to vendor data. Resolved." */
export function vendorLineAdditionToast(
  eManifestValue: string | null | undefined,
  savedStatus: string | null | undefined
): string {
  const added = `Added ${describeWasteLines(eManifestValue)} to vendor data.`;
  return savedStatus === "RESOLVED" ? `${added} Resolved.` : added;
}
